import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, RefreshCw, Trash2, UploadCloud } from "lucide-react";
import { api, formatApiError, imgSrc } from "../lib/api";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export const PhotoUploader = ({ value, onChange, folder = "dishes", label = "Photo", testid = "photo" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Please choose a JPEG, PNG, WEBP or GIF image.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/upload?folder=${folder}`, fd);
      onChange(data.url);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <div data-testid={`${testid}-uploader`}>
      {label && <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">{label}</div>}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid={`${testid}-file`}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
      />
      {value ? (
        <div className="group relative overflow-hidden rounded-2xl border border-inkline" data-testid={`${testid}-preview-wrap`}>
          <img src={imgSrc(value)} alt="Preview" className="h-44 w-full object-cover" data-testid={`${testid}-preview`} />
          <div className="absolute inset-x-0 bottom-0 flex gap-2 bg-gradient-to-t from-ink/80 to-transparent p-3">
            <button
              type="button"
              data-testid={`${testid}-replace-btn`}
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full bg-parchment px-3.5 py-1.5 text-xs font-bold text-ink transition-colors hover:bg-saffron disabled:opacity-60"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {uploading ? "Uploading…" : "Replace from device"}
            </button>
            <button
              type="button"
              data-testid={`${testid}-remove-btn`}
              onClick={() => onChange("")}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-ink/60 px-3.5 py-1.5 text-xs font-bold text-parchment backdrop-blur transition-colors hover:bg-spice"
            >
              <Trash2 size={13} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testid}-dropzone`}
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`flex h-44 w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-center transition-all ${
            dragging ? "border-spice bg-spice/5" : "border-inkline bg-cream hover:border-spice/50"
          } disabled:opacity-60`}
        >
          {uploading ? <Loader2 size={24} className="animate-spin text-spice" /> : <UploadCloud size={24} className="text-spice" />}
          <span className="text-sm font-semibold text-ink">
            {uploading ? "Uploading…" : "Drop a photo here or click to browse"}
          </span>
          <span className="text-xs text-muted2">JPEG, PNG, WEBP or GIF · up to 10MB</span>
        </button>
      )}
      <div className="mt-2 flex items-center gap-2">
        <ImagePlus size={14} className="shrink-0 text-muted2" />
        <input
          data-testid={`${testid}-url`}
          placeholder="…or paste an image URL"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-inkline bg-parchment px-3 py-2 text-xs text-ink outline-none transition-colors placeholder:text-muted2/60 focus:border-spice"
        />
      </div>
    </div>
  );
};
